import React, { useState, useEffect } from 'react';
import {
    Modal,
    Table,
    Spin,
    message,
    DatePicker,
    Select,
    Button,
    Space,
    Tag,
    Typography,
    Row,
    Col,
    Card,
    Descriptions,
    Empty,
    Tooltip
} from 'antd';
import {
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    CalendarOutlined,
    MedicineBoxOutlined,
    UserOutlined,
    WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import medpassService from '../../../services/medpassService';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AdministrationHistoryModal = ({
    visible,
    onClose,
    patientId,
    patientName = "Patient"
}) => {
    const [loading, setLoading] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateRange, setDateRange] = useState([]);

    useEffect(() => {
        if (visible && patientId) {
            fetchHistory();
        }
    }, [visible, patientId]);

    useEffect(() => {
        applyFilters();
    }, [historyData, statusFilter, dateRange]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const startDate = dateRange[0]?.format('YYYY-MM-DD');
            const endDate = dateRange[1]?.format('YYYY-MM-DD');

            const response = await medpassService.getMedicationAdministrationHistory(
                patientId,
                startDate,
                endDate
            );

            if (response.success) {
                setHistoryData(response.data || []);
            } else {
                message.error(response.message || 'Failed to fetch administration history');
                setHistoryData([]);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
            message.error('Failed to fetch administration history');
            setHistoryData([]);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...historyData];

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(record => {
                const status = record.administration_status || record.status || 'unknown';
                return status === statusFilter;
            });
        }

        // Date range filter (if not already applied in API call)
        if (dateRange.length === 2) {
            const startDate = dateRange[0];
            const endDate = dateRange[1];
            filtered = filtered.filter(record => {
                const adminDate = dayjs(record.administered_at);
                return adminDate.isAfter(startDate) && adminDate.isBefore(endDate.add(1, 'day'));
            });
        }

        setFilteredData(filtered);
    };

    const getStatusConfig = (status) => {
        const configs = {
            'administered_on_time': {
                color: 'success',
                text: 'On Time',
                icon: <CheckCircleOutlined />
            },
            'administered_early': {
                color: 'success',
                text: 'Early',
                icon: <CheckCircleOutlined />
            },
            'administered_late': {
                color: 'warning',
                text: 'Late',
                icon: <WarningOutlined />
            },
            'missed': {
                color: 'error',
                text: 'Missed',
                icon: <CloseCircleOutlined />
            },
            'cancelled': {
                color: 'default',
                text: 'Cancelled',
                icon: <CloseCircleOutlined />
            },
            'administered': {
                color: 'success',
                text: 'Administered',
                icon: <CheckCircleOutlined />
            }
        };
        return configs[status] || {
            color: 'default',
            text: status || 'Unknown',
            icon: <ExclamationCircleOutlined />
        };
    };

    const showDetails = (record) => {
        setSelectedRecord(record);
        setDetailsVisible(true);
    };

    const columns = [
        {
            title: 'Date & Time',
            key: 'administered_at',
            render: (_, record) => (
                <div>
                    <div>
                        <CalendarOutlined style={{ marginRight: 8 }} />
                        {record.administered_at ? dayjs(record.administered_at).format('YYYY-MM-DD') : '—'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {record.administered_at ? dayjs(record.administered_at).format('hh:mm A') : '—'}
                    </div>
                    {/* Show two-step administration info if available */}
                    {record.administration_start_time && record.administration_duration_minutes && (
                        <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 2 }}>
                            Duration: {record.administration_duration_minutes}min
                            {record.administration_handoff_occurred && ' • Handoff'}
                        </div>
                    )}
                </div>
            ),
            sorter: (a, b) => (a.administered_at && b.administered_at ? dayjs(a.administered_at).unix() - dayjs(b.administered_at).unix() : 0),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Medication',
            key: 'medication',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>
                        <MedicineBoxOutlined style={{ marginRight: 8 }} />
                        {record.medication_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        {record.dose_administered} via {record.route_used}
                    </div>
                </div>
            )
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const status = record.administration_status || record.status || 'administered';
                const config = getStatusConfig(status);
                return (
                    <Tag
                        color={config.color}
                        icon={config.icon}
                    >
                        {config.text}
                    </Tag>
                );
            },
            filters: [
                { text: 'On Time', value: 'administered_on_time' },
                { text: 'Early', value: 'administered_early' },
                { text: 'Late', value: 'administered_late' },
                { text: 'Missed', value: 'missed' },
                { text: 'Cancelled', value: 'cancelled' },
                { text: 'Administered', value: 'administered' }
            ],
            onFilter: (value, record) => {
                const status = record.administration_status || record.status || 'administered';
                return status === value;
            }
        },
        {
            title: 'Administered By',
            key: 'administrator',
            render: (_, record) => (
                <div>
                    <UserOutlined style={{ marginRight: 8 }} />
                    {record.administrator_name || 'Unknown'}
                </div>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="link"
                    onClick={() => showDetails(record)}
                    size="small"
                >
                    View Details
                </Button>
            )
        }
    ];

    const renderDetailsModal = () => (
        <Modal
            title="Administration Details"
            open={detailsVisible}
            onCancel={() => setDetailsVisible(false)}
            footer={[
                <Button key="close" onClick={() => setDetailsVisible(false)}>
                    Close
                </Button>
            ]}
            width={800}
            zIndex={6001}
            style={{ zIndex: 6001 }}
            maskStyle={{ zIndex: 6000 }}
        >
            {selectedRecord && (
                <div>
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Text strong>Medication:</Text>
                                <div>{selectedRecord.medication_name}</div>
                            </Col>
                            <Col span={12}>
                                <Text strong>Status:</Text>
                                <div>
                                    {(() => {
                                        const status = selectedRecord.administration_status || selectedRecord.status || 'administered';
                                        const config = getStatusConfig(status);
                                        return (
                                            <Tag color={config.color} icon={config.icon}>
                                                {config.text}
                                            </Tag>
                                        );
                                    })()}
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    <Descriptions column={2} size="small">
                        <Descriptions.Item label="Scheduled Time">
                            {selectedRecord.scheduled_time
                                ? dayjs(selectedRecord.scheduled_time).format('DD/MM/YYYY hh:mm A')
                                : 'Not specified'
                            }
                        </Descriptions.Item>
                        <Descriptions.Item label="Documented At">
                            {dayjs(selectedRecord.administered_at).format('DD/MM/YYYY hh:mm A')}
                        </Descriptions.Item>

                        {/* Two-step administration timing details */}
                        {selectedRecord.administration_start_time && (
                            <>
                                <Descriptions.Item label="Administration Started">
                                    <div>
                                        {dayjs(selectedRecord.administration_start_time).format('DD/MM/YYYY hh:mm A')}
                                        {selectedRecord.start_nurse_name && (
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                by {selectedRecord.start_nurse_name}
                                            </div>
                                        )}
                                    </div>
                                </Descriptions.Item>
                                <Descriptions.Item label="Administration Ended">
                                    <div>
                                        {selectedRecord.administration_end_time
                                            ? dayjs(selectedRecord.administration_end_time).format('DD/MM/YYYY hh:mm A')
                                            : 'In progress'
                                        }
                                        {selectedRecord.administrator_name && (
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                by {selectedRecord.administrator_name}
                                            </div>
                                        )}
                                    </div>
                                </Descriptions.Item>
                                <Descriptions.Item label="Duration">
                                    {selectedRecord.administration_duration_minutes
                                        ? `${selectedRecord.administration_duration_minutes} minutes`
                                        : 'Not calculated'
                                    }
                                    {selectedRecord.administration_handoff_occurred && (
                                        <div style={{ fontSize: '12px', color: '#f39c12' }}>
                                            <WarningOutlined style={{ marginRight: 4 }} />
                                            Nurse handoff occurred
                                        </div>
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Actual Admin Time">
                                    {selectedRecord.actual_administration_time
                                        ? dayjs(selectedRecord.actual_administration_time).format('DD/MM/YYYY hh:mm A')
                                        : 'Not specified'
                                    }
                                </Descriptions.Item>
                            </>
                        )}

                        {/* Single administration timing (fallback for non-two-step) */}
                        {!selectedRecord.administration_start_time && selectedRecord.actual_administration_time && (
                            <Descriptions.Item label="Actual Admin Time">
                                {dayjs(selectedRecord.actual_administration_time).format('DD/MM/YYYY hh:mm A')}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Dose Administered">
                            {selectedRecord.dose_administered || 'Not specified'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Route">
                            {selectedRecord.route_used || 'Not specified'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Method">
                            {selectedRecord.administration_method || 'Not specified'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Administered By">
                            {selectedRecord.administrator_name || 'Unknown'}
                        </Descriptions.Item>
                        {selectedRecord.patient_response && (
                            <Descriptions.Item label="Patient Response" span={2}>
                                {selectedRecord.patient_response}
                            </Descriptions.Item>
                        )}
                        {selectedRecord.administration_notes && (
                            <Descriptions.Item label="Notes" span={2}>
                                {selectedRecord.administration_notes}
                            </Descriptions.Item>
                        )}
                        {selectedRecord.justification && (
                            <Descriptions.Item label="Justification" span={2}>
                                {selectedRecord.justification}
                            </Descriptions.Item>
                        )}
                        {selectedRecord.side_effects_observed && selectedRecord.side_effects_observed.length > 0 && (
                            <Descriptions.Item label="Side Effects" span={2}>
                                {selectedRecord.side_effects_observed.join(', ')}
                            </Descriptions.Item>
                        )}
                    </Descriptions>

                    {/* Vitals Section */}
                    {((selectedRecord.pre_vitals && selectedRecord.pre_vitals.length > 0) ||
                      (selectedRecord.post_vitals && selectedRecord.post_vitals.length > 0)) && (
                        <Card size="small" title="Vital Signs" style={{ marginTop: 16 }}>
                            {selectedRecord.pre_vitals && selectedRecord.pre_vitals.length > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    <Text strong>Pre-Administration Vitals:</Text>
                                    {selectedRecord.pre_vitals.map((vital, index) => (
                                        <div key={index} style={{ marginLeft: 16 }}>
                                            {vital.type}: {vital.value} {vital.unit}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {selectedRecord.post_vitals && selectedRecord.post_vitals.length > 0 && (
                                <div>
                                    <Text strong>Post-Administration Vitals:</Text>
                                    {selectedRecord.post_vitals.map((vital, index) => (
                                        <div key={index} style={{ marginLeft: 16 }}>
                                            {vital.type}: {vital.value} {vital.unit}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    )}
                </div>
            )}
        </Modal>
    );

    return (
        <>
            <Modal
                title={`Administration History - ${patientName}`}
                open={visible}
                onCancel={onClose}
                width={1200}
                footer={[
                    <Button key="close" onClick={onClose}>
                        Close
                    </Button>
                ]}
                zIndex={6000}
                style={{ zIndex: 6000 }}
                maskStyle={{ zIndex: 5999 }}
            >
                <div style={{ marginBottom: 16 }}>
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                placeholder={['Start Date', 'End Date']}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col span={8}>
                            <Select
                                value={statusFilter}
                                onChange={setStatusFilter}
                                style={{ width: '100%' }}
                                placeholder="Filter by status"
                            >
                                <Option value="all">All Statuses</Option>
                                <Option value="administered_on_time">On Time</Option>
                                <Option value="administered_early">Early</Option>
                                <Option value="administered_late">Late</Option>
                                <Option value="missed">Missed</Option>
                                <Option value="cancelled">Cancelled</Option>
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Button type="primary" onClick={fetchHistory} loading={loading}>
                                Refresh
                            </Button>
                        </Col>
                    </Row>
                </div>

                <Spin spinning={loading}>
                    {filteredData.length === 0 ? (
                        <Empty
                            description="No administration history found"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={filteredData}
                            rowKey="administration_id"
                            size="middle"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} administrations`
                            }}
                        />
                    )}
                </Spin>
            </Modal>

            {renderDetailsModal()}
        </>
    );
};

export default AdministrationHistoryModal;